import {
  type ButtonHTMLAttributes,
  Children,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { tv } from 'tailwind-variants';

import Image from '../atoms/Image';
import Tooltip from '../atoms/Tooltip';
import Text from '../base/TextNew';
import ChevronDownIcon from '../icons/ChevronDownIcon';
import BaseCheckboxButton, {
  type CheckboxButtonProps as BaseCheckboxButtonProps,
} from '../input/CheckboxButton';

type OverlayPlacement = 'start' | 'end';
type OverlayDensity = 'default' | 'compact';

interface OverlayRootProps {
  children?: ReactNode;
  className?: string;
  density?: OverlayDensity;
  disabled?: boolean;
  readOnly?: boolean;
  stackClassName?: string;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

interface OverlayPanelProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  collapsed?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  placement?: OverlayPlacement;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  widthClassName?: string;
}

interface OverlayPanelHeaderProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  children?: ReactNode;
  icon?: ReactNode;
  title?: ReactNode;
  trailing?: ReactNode;
}

interface OverlayContentProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

interface OverlayGroupProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  orientation?: 'horizontal' | 'vertical';
}

interface OverlayButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: ReactNode;
  loading?: boolean;
  text?: ReactNode;
  tooltipText?: ReactNode;
}

interface OverlayCheckboxButtonProps extends BaseCheckboxButtonProps {
  tooltipText?: ReactNode;
}

interface OverlayPreviewButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  fallback?: ReactNode;
  icon?: ReactNode;
  imageAlt?: string;
  imageSrc?: string;
  crossOrigin?: 'anonymous' | 'use-credentials';
  text?: ReactNode;
  tooltipText?: ReactNode;
}

interface OverlayDividerProps {
  className?: string;
}

interface OverlayStatusProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  tone?: 'default' | 'active' | 'warning';
}

interface OverlayShortcutHintProps extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode;
}

interface OverlayRootContextValue {
  density: OverlayDensity;
  disabled: boolean;
  readOnly: boolean;
  tooltipSide: 'top' | 'right' | 'bottom' | 'left';
}

interface OverlayPanelContextValue {
  collapsed: boolean;
  collapsible: boolean;
  tooltipSide: 'top' | 'right' | 'bottom' | 'left';
  toggleCollapsed: () => void;
}

interface OverlayPlacedComponent {
  overlayPlacement?: OverlayPlacement;
}

interface OverlayStackProps {
  children: ReactNode;
  className: string;
  placement: OverlayPlacement;
}

const placements: OverlayPlacement[] = ['start', 'end'];

const OverlayRootContext = createContext<OverlayRootContextValue>({
  density: 'default',
  disabled: false,
  readOnly: false,
  tooltipSide: 'left',
});

const OverlayPanelContext = createContext<OverlayPanelContextValue | null>(
  null,
);

const overlayRoot = tv({
  base: 'pointer-events-none absolute inset-0 z-10',
});

const overlayStack = tv({
  base: 'pointer-events-none absolute inset-y-0 flex flex-col px-4 max-sm:max-w-[calc(50vw-1.25rem)]',
  variants: {
    placement: {
      start: 'left-0 items-start',
      end: 'right-0 items-end',
    },
  },
});

const overlayScrollArea = tv({
  base: 'no-scrollbar -mx-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4',
});

const overlayScrollbar = tv({
  base: 'pointer-events-auto absolute inset-y-4 z-10 w-1 rounded-full bg-white/15 opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:opacity-100',
  variants: {
    placement: {
      start: 'left-[6px]',
      end: 'right-[6px]',
    },
    visible: {
      true: 'opacity-100',
      false: '',
    },
  },
});

const overlayScrollbarThumb = tv({
  base: 'absolute left-0 w-full rounded-full bg-white/40 shadow-[0_1px_2px_rgba(0,0,0,0.16)] transition-colors hover:bg-white/65',
});

const overlayPanel = tv({
  base: 'text-primary dark:border-gray-75/40 dark:bg-gray-90/40 pointer-events-auto flex max-w-full shrink-0 flex-col overflow-hidden rounded-2xl bg-white/40 shadow-[0_8px_24px_rgba(0,0,0,0.12)] backdrop-blur-[20px] transition-[background-color,box-shadow,transform,width] duration-200 ease-out [direction:ltr]',
});

const overlayPanelHeader = tv({
  base: 'flex items-center gap-2 px-3 py-2 transition-colors',
  variants: {
    collapsed: {
      true: 'dark:hover:bg-gray-90/25 hover:bg-white/25',
      false:
        'hover:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.25)_0%,rgba(255,255,255,0.25)_calc(100%-16px),transparent_100%)] dark:hover:bg-[linear-gradient(to_bottom,rgba(34,34,34,0.25)_0%,rgba(34,34,34,0.25)_calc(100%-16px),transparent_100%)]',
    },
    collapsible: {
      true: 'focus-visible dark:active:bg-gray-90/35 cursor-pointer active:bg-white/35',
      false: '',
    },
  },
});

const overlayPanelHeaderArrow = tv({
  base: 'inline-block transition-transform duration-200 ease-out',
  variants: {
    collapsed: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const overlayContent = tv({
  base: 'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
  variants: {
    collapsed: {
      true: 'grid-rows-[0fr] opacity-0',
      false: 'grid-rows-[1fr] opacity-100',
    },
  },
});

const overlayContentInner = tv({
  base: 'flex flex-col gap-2 p-2 pt-0.5',
});

const overlayGroup = tv({
  base: 'flex gap-1',
  variants: {
    orientation: {
      horizontal: 'flex-row items-center',
      vertical: 'flex-col',
    },
  },
});

const overlayButton = tv({
  base: 'focus-visible text-primary flex min-h-[30px] min-w-[30px] flex-1 items-center justify-center gap-1 rounded-lg px-1.5 py-[7px] transition-all',
  variants: {
    active: {
      true: 'dark:bg-gray-90 bg-white',
      false:
        'dark:bg-gray-90/25 dark:hover:bg-gray-90/45 bg-white/25 hover:bg-white/45',
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: 'cursor-pointer active:scale-[0.98]',
    },
  },
});

const overlayCheckboxButton = tv({
  base: 'text-primary dark:bg-gray-90/25 flex w-full justify-center border-0 bg-white/25',
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false:
        'dark:hover:bg-gray-90/45 cursor-pointer hover:bg-white/45 active:scale-[0.98]',
    },
  },
});

const overlayPreviewButton = tv({
  base: 'text-primary flex w-full flex-col items-start gap-1 rounded-lg px-1.5 py-1.5 transition-colors',
  variants: {
    active: {
      true: 'dark:bg-gray-90 bg-white',
      false:
        'dark:bg-gray-90/25 dark:hover:bg-gray-90/45 bg-white/25 hover:bg-white/45',
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50',
      false: 'cursor-pointer active:scale-[0.98]',
    },
  },
});

const overlayDivider = tv({
  base: 'dark:bg-gray-75/50 -mx-2 h-px border-0 bg-white/50',
});

const overlayStatus = tv({
  base: 'rounded-lg px-2 py-1',
  variants: {
    tone: {
      default: 'text-secondary dark:bg-gray-90/25 bg-white/25',
      active: 'bg-blue/15 text-primary',
      warning: 'text-primary bg-yellow-50/25',
    },
  },
});

const overlayShortcutHint = tv({
  base: 'text-secondary dark:bg-gray-90/35 rounded-md bg-white/35 px-1.5 py-0.5',
});

function Root({
  children,
  className = '',
  density = 'default',
  disabled = false,
  readOnly = false,
  stackClassName = '',
  tooltipSide = 'left',
}: OverlayRootProps) {
  const panelsByPlacement = groupPanelsByPlacement(children);

  return (
    <OverlayRootContext.Provider
      value={{ density, disabled, readOnly, tooltipSide }}
    >
      <div className={overlayRoot({ className })}>
        {placements.map((placement) => {
          const panels = panelsByPlacement[placement];
          if (panels.length === 0) {
            return null;
          }

          return (
            <OverlayStack
              key={placement}
              className={stackClassName}
              placement={placement}
            >
              {panels}
            </OverlayStack>
          );
        })}
      </div>
    </OverlayRootContext.Provider>
  );
}

function OverlayStack({ children, className, placement }: OverlayStackProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const scrollbarHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [scrollMetrics, setScrollMetrics] = useState({
    clientHeight: 0,
    scrollHeight: 0,
    scrollTop: 0,
  });
  const [isScrollbarVisible, setScrollbarVisible] = useState(false);

  const revealScrollbar = () => {
    if (scrollbarHideTimerRef.current) {
      clearTimeout(scrollbarHideTimerRef.current);
    }
    setScrollbarVisible(true);
    scrollbarHideTimerRef.current = setTimeout(() => {
      setScrollbarVisible(false);
    }, 700);
  };

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const updateScrollMetrics = () => {
      setScrollMetrics((current) => {
        const next = {
          clientHeight: scrollArea.clientHeight,
          scrollHeight: scrollArea.scrollHeight,
          scrollTop: scrollArea.scrollTop,
        };
        return current.clientHeight === next.clientHeight &&
          current.scrollHeight === next.scrollHeight &&
          current.scrollTop === next.scrollTop
          ? current
          : next;
      });
    };

    const resizeObserver = new ResizeObserver(updateScrollMetrics);
    resizeObserver.observe(scrollArea);
    [...scrollArea.children].forEach((child) => resizeObserver.observe(child));
    const handleScroll = () => {
      updateScrollMetrics();
      if (scrollbarHideTimerRef.current) {
        clearTimeout(scrollbarHideTimerRef.current);
      }
      setScrollbarVisible(true);
      scrollbarHideTimerRef.current = setTimeout(() => {
        setScrollbarVisible(false);
      }, 700);
    };
    scrollArea.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    updateScrollMetrics();

    return () => {
      resizeObserver.disconnect();
      scrollArea.removeEventListener('scroll', handleScroll);
      if (scrollbarHideTimerRef.current) {
        clearTimeout(scrollbarHideTimerRef.current);
      }
    };
  }, []);

  const hasOverflow = scrollMetrics.scrollHeight > scrollMetrics.clientHeight;
  const thumbHeight = hasOverflow
    ? Math.max(
        24,
        (scrollMetrics.clientHeight / scrollMetrics.scrollHeight) * 100,
      )
    : 0;
  const thumbTop = hasOverflow
    ? (scrollMetrics.scrollTop /
        (scrollMetrics.scrollHeight - scrollMetrics.clientHeight)) *
      (100 - thumbHeight)
    : 0;

  const scrollToPointer = (
    event: PointerEvent<HTMLDivElement>,
    scrollbar: HTMLDivElement,
  ) => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const scrollbarRect = scrollbar.getBoundingClientRect();
    const thumbHeightPx = Math.max(
      24,
      (scrollArea.clientHeight / scrollArea.scrollHeight) *
        scrollbarRect.height,
    );
    const trackRange = scrollbarRect.height - thumbHeightPx;
    const pointerOffset = event.clientY - scrollbarRect.top - thumbHeightPx / 2;
    const thumbOffset = Math.min(Math.max(pointerOffset, 0), trackRange);
    scrollArea.scrollTop =
      (thumbOffset / trackRange) *
      (scrollArea.scrollHeight - scrollArea.clientHeight);
  };

  const handleScrollbarPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    revealScrollbar();
    scrollToPointer(event, event.currentTarget);
  };

  const handleScrollbarPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    scrollToPointer(event, event.currentTarget);
  };

  return (
    <div className={overlayStack({ placement, className })}>
      <div ref={scrollAreaRef} className={overlayScrollArea()}>
        <div
          aria-hidden
          className="-mb-1 h-4 flex-none"
          data-overlay-scroll-start-spacer
        />
        {children}
        <div
          aria-hidden
          className="-mt-1 h-4 flex-none"
          data-overlay-scroll-end-spacer
        />
      </div>
      {hasOverflow && (
        <div
          aria-hidden
          className={overlayScrollbar({
            placement,
            visible: isScrollbarVisible,
          })}
          onPointerDown={handleScrollbarPointerDown}
          onPointerMove={handleScrollbarPointerMove}
          onPointerEnter={revealScrollbar}
        >
          <div
            className={overlayScrollbarThumb()}
            style={{ height: `${thumbHeight}%`, top: `${thumbTop}%` }}
          />
        </div>
      )}
    </div>
  );
}

function Panel({
  children,
  className = '',
  collapsed,
  collapsible = false,
  defaultCollapsed = false,
  id,
  onClick,
  onCollapsedChange,
  onPointerDown,
  placement = 'end',
  tooltipSide,
  widthClassName = 'w-[170px]',
  ...props
}: OverlayPanelProps) {
  const [uncontrolledCollapsed, setUncontrolledCollapsed] =
    useState(defaultCollapsed);
  const isControlled = collapsed !== undefined;
  const resolvedCollapsed = isControlled ? collapsed : uncontrolledCollapsed;

  const toggleCollapsed = () => {
    if (!collapsible) {
      return;
    }

    const nextCollapsed = !resolvedCollapsed;
    if (!isControlled) {
      setUncontrolledCollapsed(nextCollapsed);
    }
    onCollapsedChange?.(nextCollapsed);
  };
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onPointerDown?.(event);
  };
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onClick?.(event);
  };
  return (
    <OverlayPanelContext.Provider
      value={{
        collapsed: resolvedCollapsed,
        collapsible,
        tooltipSide: tooltipSide ?? getTooltipSideForPlacement(placement),
        toggleCollapsed,
      }}
    >
      <div
        id={id}
        data-overlay-placement={placement}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        className={overlayPanel({
          className: `${widthClassName} ${className}`,
        })}
        {...props}
      >
        {children}
      </div>
    </OverlayPanelContext.Provider>
  );
}

function PanelHeader({
  children,
  className = '',
  icon,
  onClick,
  onKeyDown,
  title,
  trailing,
  ...props
}: OverlayPanelHeaderProps) {
  const panel = useContext(OverlayPanelContext);
  const collapsed = panel?.collapsed ?? false;
  const collapsible = panel?.collapsible ?? false;
  const clickable = collapsible || Boolean(onClick);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (collapsible) {
      panel?.toggleCollapsed();
    }

    onClick?.(event);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (clickable && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      if (collapsible) {
        panel?.toggleCollapsed();
      }
      if (!collapsible) {
        event.currentTarget.click();
      }
    }

    onKeyDown?.(event);
  };

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-expanded={collapsible ? !collapsed : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={overlayPanelHeader({
        collapsed: collapsible && collapsed,
        collapsible: clickable,
        className,
      })}
      {...props}
    >
      {icon && (
        <span className="flex size-5 flex-none items-center justify-center">
          {icon}
        </span>
      )}
      {title !== undefined && (
        <Text
          token="12/medium"
          className="text-primary min-w-0 flex-1 truncate"
        >
          {title}
        </Text>
      )}
      {children}
      {trailing}
      {collapsible && (
        <span className="text-primary flex size-5 flex-none items-center justify-center rounded-md">
          <span aria-hidden className={overlayPanelHeaderArrow({ collapsed })}>
            <ChevronDownIcon className="size-4" />
          </span>

          <span className="sr-only">
            {collapsed ? 'Expand overlay panel' : 'Collapse overlay panel'}
          </span>
        </span>
      )}
    </div>
  );
}

function Content({ children, className = '', ...props }: OverlayContentProps) {
  const collapsed = useContext(OverlayPanelContext)?.collapsed ?? false;

  return (
    <div className={overlayContent({ collapsed, className })} {...props}>
      <div className="min-h-0 overflow-hidden">
        <div className={overlayContentInner()}>{children}</div>
      </div>
    </div>
  );
}

function Group({
  children,
  className = '',
  orientation = 'horizontal',
  ...props
}: OverlayGroupProps) {
  return (
    <div className={overlayGroup({ orientation, className })} {...props}>
      {children}
    </div>
  );
}

function Button({
  active = false,
  children,
  className = '',
  disabled = false,
  icon,
  loading = false,
  text,
  tooltipText,
  ...props
}: OverlayButtonProps) {
  const root = useContext(OverlayRootContext);
  const panel = useContext(OverlayPanelContext);
  const isDisabled = disabled || root.disabled || root.readOnly || loading;
  const button = (
    <button
      type="button"
      className={overlayButton({
        active,
        disabled: isDisabled,
        className,
      })}
      disabled={isDisabled}
      {...props}
    >
      {icon && (
        <span className="flex size-5 flex-none items-center justify-center">
          {icon}
        </span>
      )}
      {text !== undefined && (
        <Text
          token="12/medium"
          className={`min-w-0 flex-1 truncate ${icon ? 'text-left' : 'text-center'}`}
        >
          {loading ? 'Loading…' : text}
        </Text>
      )}
      {children}
    </button>
  );

  return wrapTooltip(
    button,
    tooltipText,
    panel?.tooltipSide ?? root.tooltipSide,
  );
}

function CheckboxButton({
  checked,
  className = '',
  disabled = false,
  tooltipText,
  ...props
}: OverlayCheckboxButtonProps) {
  const root = useContext(OverlayRootContext);
  const panel = useContext(OverlayPanelContext);
  const isDisabled = disabled || root.disabled || root.readOnly;
  const button = (
    <BaseCheckboxButton
      checked={checked}
      className={overlayCheckboxButton({
        disabled: isDisabled,
        className,
      })}
      disabled={isDisabled}
      {...props}
    />
  );

  return wrapTooltip(
    button,
    tooltipText,
    panel?.tooltipSide ?? root.tooltipSide,
  );
}

function PreviewButton({
  active = false,
  children,
  className = '',
  disabled = false,
  fallback,
  icon,
  imageAlt = '',
  imageSrc,
  crossOrigin,
  text,
  tooltipText,
  ...props
}: OverlayPreviewButtonProps) {
  const root = useContext(OverlayRootContext);
  const panel = useContext(OverlayPanelContext);
  const isDisabled = disabled || root.disabled || root.readOnly;
  const button = (
    <button
      type="button"
      className={overlayPreviewButton({
        active,
        disabled: isDisabled,
        className,
      })}
      disabled={isDisabled}
      {...props}
    >
      <div className="bg-secondary flex h-[120px] w-full items-center justify-center overflow-hidden rounded-md">
        {imageSrc ? (
          <Image
            className="h-full w-full object-cover"
            src={imageSrc}
            alt={imageAlt}
            crossOrigin={crossOrigin}
          />
        ) : (
          fallback
        )}
      </div>
      {text !== undefined && (
        <div className="flex min-w-0 items-center justify-start gap-1">
          <span className="flex size-5 flex-none items-center justify-center">
            {icon}
          </span>
          <Text token="12/medium" className="text-primary truncate">
            {text}
          </Text>
        </div>
      )}
      {children}
    </button>
  );

  return wrapTooltip(
    button,
    tooltipText,
    panel?.tooltipSide ?? root.tooltipSide,
  );
}

function Divider({ className = '' }: OverlayDividerProps) {
  return <hr className={overlayDivider({ className })} />;
}

function ShortcutHint({
  children,
  className = '',
  ...props
}: OverlayShortcutHintProps) {
  return (
    <Text
      as="span"
      token="12/medium"
      className={overlayShortcutHint({ className })}
      {...props}
    >
      {children}
    </Text>
  );
}

function groupPanelsByPlacement(children: ReactNode) {
  const panelsByPlacement: Record<OverlayPlacement, ReactNode[]> = {
    start: [],
    end: [],
  };

  Children.forEach(children, (child) => {
    if (!isValidElement<OverlayPanelProps>(child)) {
      return;
    }

    const placement =
      child.props.placement ??
      (child.type as OverlayPlacedComponent).overlayPlacement ??
      'end';
    panelsByPlacement[placement].push(child);
  });

  return panelsByPlacement;
}

function getTooltipSideForPlacement(placement: OverlayPlacement) {
  return placement === 'start' ? 'right' : 'left';
}

function wrapTooltip(
  button: ReactElement,
  tooltipText: ReactNode | undefined,
  tooltipSide: 'top' | 'right' | 'bottom' | 'left',
) {
  if (!tooltipText) {
    return button;
  }

  return (
    <Tooltip text={tooltipText} side={tooltipSide} color="default">
      <span className="inline-flex w-full">{button}</span>
    </Tooltip>
  );
}

const Overlay = {
  Root,
  Panel,
  PanelHeader,
  Content,
  Group,
  Button,
  CheckboxButton,
  PreviewButton,
  Divider,
  ShortcutHint,
};

export default Overlay;
